<template>
    <div class="findly-mobile-nav" :class="{ 'is-open': isOpen }" id="findly-mobile-nav" role="dialog" aria-label="Mobile Navigation" aria-hidden="!isOpen">

        <!-- Overlay -->
        <div class="findly-mobile-nav__overlay" @click="close" aria-hidden="true"></div>

        <!-- Slide-In Panel -->
        <div class="findly-mobile-nav__panel">

            <!-- Panel Header -->
            <div class="findly-mobile-nav__header">
                <button class="findly-mobile-nav__close btn btn-link" @click="close" :aria-label="$translate('Ceres::Template.closeIcon')">
                    <i class="fa fa-times" aria-hidden="true"></i>
                </button>
                <span class="findly-mobile-nav__title">
                    <template v-if="currentLevel > 0 && activeParent">
                        <button class="findly-mobile-nav__back btn btn-link" @click="goBack">
                            <i class="fa fa-chevron-left" aria-hidden="true"></i>
                        </button>
                        {{ activeParent.name }}
                    </template>
                    <template v-else>
                        Menü
                    </template>
                </span>
            </div>

            <!-- Navigation Level 0 – Top Categories -->
            <div class="findly-mobile-nav__body">
                <transition :name="slideDirection">
                    <ul v-if="currentLevel === 0"
                        key="level-0"
                        class="findly-mobile-nav__list list-unstyled mb-0">
                        <li v-for="category in navigationTree"
                            :key="category.id"
                            class="findly-mobile-nav__item">
                            <a :href="category.url"
                               class="findly-mobile-nav__link"
                               v-if="!category.children || category.children.length === 0">
                                {{ category.name }}
                            </a>
                            <div v-else class="findly-mobile-nav__link-wrap d-flex">
                                <a :href="category.url" class="findly-mobile-nav__link flex-fill">
                                    {{ category.name }}
                                </a>
                                <button class="findly-mobile-nav__toggle btn btn-link"
                                        @click="openLevel(category)"
                                        :aria-label="'Untermenü öffnen: ' + category.name">
                                    <i class="fa fa-chevron-right" aria-hidden="true"></i>
                                </button>
                            </div>
                        </li>
                    </ul>

                    <!-- Navigation Level 1+ – Subcategories -->
                    <ul v-else
                        key="level-sub"
                        class="findly-mobile-nav__list list-unstyled mb-0">
                        <li class="findly-mobile-nav__item findly-mobile-nav__item--all">
                            <a :href="activeParent.url" class="findly-mobile-nav__link findly-mobile-nav__link--all">
                                <i class="fa fa-th-large mr-2" aria-hidden="true"></i>
                                Alle {{ activeParent.name }} anzeigen
                            </a>
                        </li>
                        <li v-for="child in activeChildren"
                            :key="child.id"
                            class="findly-mobile-nav__item">
                            <a :href="child.url"
                               class="findly-mobile-nav__link"
                               v-if="!child.children || child.children.length === 0">
                                {{ child.name }}
                            </a>
                            <div v-else class="findly-mobile-nav__link-wrap d-flex">
                                <a :href="child.url" class="findly-mobile-nav__link flex-fill">
                                    {{ child.name }}
                                </a>
                                <button class="findly-mobile-nav__toggle btn btn-link"
                                        @click="openLevel(child)"
                                        :aria-label="'Untermenü öffnen: ' + child.name">
                                    <i class="fa fa-chevron-right" aria-hidden="true"></i>
                                </button>
                            </div>
                        </li>
                    </ul>
                </transition>
            </div>

        </div>
    </div>
</template>

<script>
export default {
    name: "FindlyMobileNavigation",

    props: {
        initialCategory: {
            type: Object,
            default: null
        },
        includeLanguage: {
            type: Boolean,
            default: false
        },
        // Rohe Ceres-Kategorie-Struktur aus Twig (categories | json_encode)
        categoriesRaw: {
            type: Array,
            default: function () { return []; }
        },
        // urls.trailingSlashSuffix aus Twig
        trailingSlash: {
            type: String,
            default: ""
        }
    },

    data() {
        return {
            isOpen:       false,
            currentLevel: 0,
            breadcrumb:   [],
        };
    },

    computed: {

        navigationTree() {
            // Primär: direkt übergebene Twig-Kategoriendaten (zuverlässig)
            if (this.categoriesRaw && this.categoriesRaw.length > 0) {
                return this._transformCategories(this.categoriesRaw, "");
            }
            // Fallback: Ceres Vuex-Store
            try {
                return this.$store.getters["navigation/navigationTree"] || [];
            } catch (e) {
                return [];
            }
        },

        activeParent() {
            if (this.breadcrumb.length === 0) return null;
            return this.breadcrumb[this.breadcrumb.length - 1];
        },

        activeChildren() {
            if (!this.activeParent) return [];
            return this.activeParent.children || [];
        },

        slideDirection() {
            return this.currentLevel > 0 ? "slide-left" : "slide-right";
        }
    },

    mounted() {
        // Lauscht auf Custom Event vom Hamburger-Button (base.js)
        this._openHandler = () => this.open();
        document.addEventListener("findly:openMobileNav", this._openHandler);
    },

    beforeDestroy() {
        document.removeEventListener("findly:openMobileNav", this._openHandler);
    },

    methods: {
        // Twig-Kategorie-Struktur (details[0].name / nameUrl) → { id, name, url, children }
        // parentPath wird kumulativ weitergegeben: /damen → /damen/homewear → /damen/homewear/pyjamas
        _transformCategories(cats, parentPath) {
            var self = this;
            return (cats || []).reduce(function (acc, cat) {
                var detail = cat.details && cat.details[0];
                if (!detail) return acc;

                var name    = detail.name || "";
                // Kategorien die mit "X-" beginnen ausblenden (Ceres-Konvention)
                if (name.indexOf("X-") === 0) name = name.slice(2);

                var segment = detail.nameUrl || "";

                // Top-Level: Sprachpräfix berücksichtigen (analog Desktop-Twig)
                // Kinder: parentPath kumulativ erweitern
                var path;
                if (parentPath === "") {
                    path = self.includeLanguage
                        ? ("/" + (detail.lang || "de") + "/" + segment)
                        : ("/" + segment);
                } else {
                    path = parentPath + "/" + segment;
                }

                acc.push({
                    id:       cat.id,
                    name:     name,
                    url:      path + self.trailingSlash,
                    children: cat.children && cat.children.length
                        ? self._transformCategories(cat.children, path)
                        : []
                });
                return acc;
            }, []);
        },

        open() {
            this.isOpen = true;
            document.body.classList.add("mobile-nav-open");
        },

        close() {
            this.isOpen       = false;
            this.currentLevel = 0;
            this.breadcrumb   = [];
            document.body.classList.remove("mobile-nav-open");
        },

        openLevel(category) {
            this.breadcrumb.push(category);
            this.currentLevel++;
        },

        goBack() {
            if (this.breadcrumb.length > 0) {
                this.breadcrumb.pop();
                this.currentLevel--;
            }
        }
    }
};
</script>
